import {useMemo, ReactNode} from 'react';
import {
  GlobeIcon,
  CircleQuestionMarkIcon,
  LockKeyholeIcon,
  LogOutIcon,
  UserIcon,
} from 'lucide-react';
import {useTranslation} from 'react-i18next';
import {useMatch} from 'react-router-dom';
import {useGuardedNavigate} from 'context/navigationGuard';
import {useReservation} from 'context/reservation';
import {usePath} from './common';
import {usePrivacyPolicyLink} from './usePrivacyPolicyLink';
import {useAuth} from './auth/useAuth';
import {goToExternalLink} from 'utils/common';
import {PATHS} from 'routes/constants';
import {closeSession, hasSession} from 'components/sessions/utils';
import {getLanguageLabel} from 'utils/i18n/constants';

export enum NavLinks {
  home = 'home',
  onlineChekin = 'sidebar_registration',
  virtualKeys = 'sidebar_virtual_keys',
  guidebooks = 'guidebooks',
  language = 'language',
  privacy = 'privacy_policy',
  faq = 'faqs',
  paymentCart = 'sidebar_payments',
  logout = 'logout',
  login = 'login',
  endSession = 'end_session',
  checkOut = 'sidebar_checkout',
  enhanceYourStay = 'sidebar_recommendations',
  chat = 'chat',
  travelGuide = 'travel_guide.sidebar_title',
  luggageStorage = 'sidebar_luggage_storage',
  travelEsim = 'sidebar_travel_esim',
}

type NavItem = {
  label: string;
  selected: boolean;
  disabled: boolean;
  icon: ReactNode;
  className?: string;
  onclick?: () => void;
  description?: string;
  value?: string;
};

const isAuthItem = (item: {label: string; className?: string}) =>
  item.className?.includes('sidebar__item-auth') ||
  item.label === NavLinks.login ||
  item.label === NavLinks.logout;

type Props = {
  onLanguageClick?: () => void;
  onAuthClick?: () => void;
  onPrivacyPolicyClick?: () => void;
  faqsEnable?: boolean;
};
function useNavigationLinks({
  onLanguageClick,
  onAuthClick,
  onPrivacyPolicyClick,
  faqsEnable = false,
}: Props) {
  const {reservationId} = useReservation();
  const {i18n} = useTranslation();
  const {isLoggedIn, user} = useAuth();
  const guardedNavigate = useGuardedNavigate();
  const {getFullPath, getMainPath} = usePath();
  const isFaqRoute = Boolean(useMatch(getMainPath(PATHS.faq)));
  const {href: privacyPolicyHref} = usePrivacyPolicyLink();

  const closeSessionItem = useMemo(() => {
    return hasSession(reservationId)
      ? {
          [NavLinks.endSession]: {
            label: NavLinks.endSession,
            selected: false,
            disabled: false,
            icon: <LogOutIcon size={18} />,
            onclick: () => closeSession(reservationId),
          },
        }
      : {};
  }, [reservationId]);

  const buildAuthItem = useMemo(() => {
    const label = isLoggedIn ? NavLinks.logout : NavLinks.login;
    const authIcon = isLoggedIn ? <LogOutIcon size={18} /> : <UserIcon size={18} />;
    const description = isLoggedIn ? user?.email : undefined;

    return {
      [NavLinks.logout]: {
        label,
        selected: false,
        disabled: false,
        icon: authIcon,
        onclick: onAuthClick,
        className: 'sidebar__item-auth',
        description,
      },
    };
  }, [onAuthClick, isLoggedIn, user?.email]);

  const navigatorLinks = useMemo(() => {
    const navigatorLinks: Record<string, NavItem> = {
      [NavLinks.language]: {
        label: NavLinks.language,
        selected: false,
        disabled: false,
        icon: <GlobeIcon size={16} />,
        onclick: onLanguageClick || (() => undefined),
        value: getLanguageLabel(i18n.language),
      },
      ...(faqsEnable && {
        [NavLinks.faq]: {
          label: NavLinks.faq,
          selected: isFaqRoute,
          disabled: false,
          icon: <CircleQuestionMarkIcon size={16} />,
          onclick: () => guardedNavigate(getFullPath(PATHS.faq)),
        },
      }),
      [NavLinks.privacy]: {
        label: NavLinks.privacy,
        selected: false,
        disabled: false,
        icon: <LockKeyholeIcon size={16} />,
        onclick: onPrivacyPolicyClick || (() => goToExternalLink(privacyPolicyHref)),
      },
    };
    return {
      ...closeSessionItem,
      ...navigatorLinks,
      ...buildAuthItem,
    };
  }, [
    onLanguageClick,
    onPrivacyPolicyClick,
    privacyPolicyHref,
    i18n.language,
    faqsEnable,
    isFaqRoute,
    guardedNavigate,
    getFullPath,
    closeSessionItem,
    buildAuthItem,
  ]);

  const navigatorLinksArray = useMemo(() => {
    return Object.values(navigatorLinks);
  }, [navigatorLinks]);

  const utilityLinks = useMemo(
    () => navigatorLinksArray.filter(item => !isAuthItem(item)),
    [navigatorLinksArray],
  );

  const authLinks = useMemo(
    () => (isLoggedIn ? [] : navigatorLinksArray.filter(isAuthItem)),
    [navigatorLinksArray, isLoggedIn],
  );

  return {utilityLinks, authLinks};
}

export {useNavigationLinks};
